import { getOwnersByOperator, getDomainsByManager } from '../logs/domain';
import { getState } from 'src/store';

// Contract Deplyoments
import ResolverDeployment from '../../contracts/deployments/Resolver.json';
import { ACTIVE_DEPLOYMENT } from '../contracts/common';
import { available } from '../contracts/registrar';
import { namehash } from 'ethers';

export const insertNode = (node: DomainTree) => {
  const domainTree = getState().domainTree;
  if (domainTree) {
    getState().setDomainTree(_buildTreeWithNode(node, domainTree));
  }
};

export const updateDomains = async () => {
  const address = getState().wallet?.address;
  const domainTree = getState().domainTree;

  const filterDomainTreeByOwner = (
    node: DomainTree,
    address: string,
  ): DomainTree[] => {
    // Filter the subdomains recursively
    const filteredSubdomains = node.subdomains.flatMap(sub =>
      filterDomainTreeByOwner(sub, address),
    );

    // If the current node's owner matches the address, return it with its filtered subdomains
    if (node.owner === address) {
      return [{ ...node, subdomains: filteredSubdomains }];
    }

    // If the current node's owner does not match the address, return the filtered subdomains (i.e., promote them)
    return filteredSubdomains;
  };

  const convertDomainTreeToDomain = (
    domainTrees: DomainTree[],
    accessRights: string,
  ): Domain[] => {
    return domainTrees.map(domainTree => {
      return {
        name: domainTree.name,
        accessRights: accessRights,
        subdomains: convertDomainTreeToDomain(
          domainTree.subdomains,
          accessRights,
        ),
        managers: [],
      };
    });
  };

  const findDomainsByHashes = (
    domainTree: DomainTree,
    hashes: string[],
  ): Domain[] => {
    const result: Domain[] = [];

    const searchDomainByHash = (
      node: DomainTree,
      targetHash: string,
    ): Domain | undefined => {
      if (node.hash === targetHash) {
        return {
          name: node.name,
          accessRights: 'manager',
          subdomains: [],
          managers: [],
        };
      }

      for (const subdomain of node.subdomains) {
        const foundDomain = searchDomainByHash(subdomain, targetHash);
        if (foundDomain) {
          return foundDomain;
        }
      }

      return undefined;
    };

    for (const hash of hashes) {
      const foundDomain = searchDomainByHash(domainTree, hash);
      if (foundDomain) {
        result.push(foundDomain);
      }
    }

    return result;
  };

  if (domainTree && address) {
    const filteredDomainTree = filterDomainTreeByOwner(domainTree, address);
    if (filteredDomainTree) {
      // Domains the user owns
      let domains = convertDomainTreeToDomain(filteredDomainTree, 'owner');
      for (const i in domains) {
        const index = Number(i);
        const domainArr = domains[index].name.split('.');
        const domainInactive = await available(domainArr[domainArr.length - 2]);
        if (domainInactive) {
          domains.splice(index, 1);
        }
      }
      // Domains the user has access rights to
      const approvedForOperator = await getOwnersByOperator(address);
      for (const i in approvedForOperator) {
        const tree = filterDomainTreeByOwner(
          domainTree,
          approvedForOperator[i],
        );
        const operatorDomains = convertDomainTreeToDomain(tree, 'operator');
        domains = domains.concat(operatorDomains);
      }
      // Domains the user has Manager rights in the resolver
      const managedDomainHashes = await getDomainsByManager(
        address,
        ResolverDeployment[ACTIVE_DEPLOYMENT].address,
      );
      const managerDomains = findDomainsByHashes(
        domainTree,
        managedDomainHashes,
      );
      domains = domains.concat(managerDomains);
      getState().setDomains(domains);
    }
  }
};

const _buildDomainTree = (
  rootLabel: string,
  logArray: string[][],
): DomainTree | undefined => {
  const tree: DomainTree | undefined = undefined;
  const rootHash = namehash(rootLabel);

  return tree;
};

const _addDomainToParent = (
  node: DomainTree,
  parentHash: string,
  label: string,
  owner: string,
): boolean => {
  if ((node.hash = parentHash)) {
    const name = label + '.' + node.name;
    const hash = namehash(name);
    const subdomain = {
      name,
      owner,
      parentHash,
      hash,
      subdomains: [],
    };
    node.subdomains.push(subdomain);
    return true;
  }
  for (const subdomain of node.subdomains) {
    if (_addDomainToParent(subdomain, parentHash, label, owner)) {
      return true;
    }
  }
  return false;
};

// Helper functions
const _buildTreeWithNode = (
  node: DomainTree,
  domainTree: DomainTree,
): DomainTree => {
  if (node.parentHash === domainTree.hash) {
    domainTree.subdomains.push(node);
    return domainTree;
  }

  for (const subdomain of domainTree.subdomains) {
    const updatedSubdomain = _buildTreeWithNode(node, subdomain);

    if (updatedSubdomain !== subdomain) {
      return domainTree;
    }
  }

  return domainTree;
};
